import { LocalDate } from '@js-joda/core';
import styled from 'styled-components';

import { Country } from '@dreryk/address-common-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import {
   GuardianPesel,
   NationalIdentificationNumberCountry,
   Pesel,
} from '@dreryk/personal-external-identifier-common-api';
import { MedicalRecordNumber } from '@dreryk/egabinet-patient-published-language-api';

import { FormContextValues, useTranslate } from '@dreryk/react-common';
import { StyledFlex } from '@dreryk/react-components';

import { IdentityDocumentFormPart } from '../identityDocument/IdentityDocumentFormSection';
import { patientTranslationKeys } from '../translations';

import { PersonalIdentifierType } from './common.types';
import { FormSectionHeader } from './FormSectionHeader';
import { GuardianModalFormBean } from './GuardianModal/GuardianModal.types';
import { BirthDate } from './PersonForm/BirthDate/BirthDate';
import { EwusPatientDownload } from './PersonForm/EwusPatientDownload/EwusPatientDownload';
import { FirstNameField } from './PersonForm/FirstNameField';
import { Gender } from './PersonForm/Gender/Gender';
import { GenderSelectItem } from './PersonForm/Gender/GenderField.hooks';
import { LastNameField } from './PersonForm/LastNameField';
import { MiddleNameField } from './PersonForm/MiddleNameField';
import {
   PatientSelectIdentifierType,
   PersonalIdentifierTypeField,
} from './PersonForm/PatientIdentifierTypeField/PatientIdentifierTypeField';
import { NationalIdentificationNumberCountryField } from './PersonForm/PersonalExternalIdentifier/NationalIdentificationNumber/NationalIdentificationNumberCountryField';
import { PassportCountryField } from './PersonForm/PersonalExternalIdentifier/Passport/PassportCountryField';
import { PassportValidToDateField } from './PersonForm/PersonalExternalIdentifier/Passport/PassportValidToField';
import { GuardianPeselField } from './PersonForm/PersonalExternalIdentifier/GuardianPesel/GuardianPeselField';
import { PassportField } from './PersonForm/PersonalExternalIdentifier/Passport/PassportField';
import { NationalIdentificationNumberField } from './PersonForm/PersonalExternalIdentifier/NationalIdentificationNumber/NationalIdentificationNumberField';
import { MedicalRecordNumberField } from './PersonForm/PersonalExternalIdentifier/MedicalRecordNumber/MedicalRecordNumberField';
import { PeselField } from './PersonForm/PersonalExternalIdentifier/Pesel/PeselField';

const StyledPersonalDataForm = styled.form`
   display: flex;
   flex-direction: column;
   gap: 30px;
`;

StyledPersonalDataForm.displayName = 'StyledPersonalDataForm';

export const StyledPersonalDataFormSectionContent = styled.div`
   display: grid;
   gap: 24px;
   grid-template-columns: repeat(2, minmax(0, 1fr));
`;

StyledPersonalDataFormSectionContent.displayName = 'StyledPersonalDataFormSectionContent';

export type PersonalDataFormBeanPart = {
   firstName: string | null;
   middleName: string | null;
   lastName: string | null;
   personalIdentifierType: PatientSelectIdentifierType;
   birthDate: LocalDate | null;
   gender: GenderSelectItem | null;
   pesel?: Pesel | null;
   guardianPesel?: GuardianPesel | null;
   identifyingGuardian?: GuardianModalFormBean;
   passport?: string | null;
   passportCountry?: Country | null;
   passportValidTo?: LocalDate | null;
   nationalIdentificationNumber?: string | null;
   nationalIdentificationNumberCountry?: NationalIdentificationNumberCountry | null;
   medicalRecordNumber?: string | null;
   identityDocument: IdentityDocumentFormPart;
};

type PersonalDataFormSectionProps = Pick<
   FormContextValues<PersonalDataFormBeanPart>,
   'watch' | 'register' | 'setValue' | 'trigger' | 'formState' | 'setError' | 'reset' | 'getValues'
> & {
   readonly patientId?: Patient['id'];
   readonly patientMedicalRecordNumber?: MedicalRecordNumber | null;
   readonly withGuardians?: boolean;
};

const StyledPersonalDataFormSection = styled.div`
   width: 100%;
   max-width: 948px;
`;
StyledPersonalDataFormSection.displayName = 'StyledPersonalDataFormSection';

// eslint-disable-next-line max-lines-per-function
export const PersonalDataFormSection = (props: PersonalDataFormSectionProps): JSX.Element => {
   const translate = useTranslate();

   const { personalIdentifierType } = props.watch(['personalIdentifierType']);

   return (
      <StyledPersonalDataFormSection>
         <FormSectionHeader>{translate(patientTranslationKeys.basicData)}</FormSectionHeader>
         <StyledPersonalDataFormSectionContent>
            <PersonalIdentifierTypeField
               register={props.register}
               withGuardians={props.withGuardians}
               getValues={props.getValues}
               reset={props.reset}
            />

            {personalIdentifierType.id === PersonalIdentifierType.PESEL && (
               <StyledFlex gap={'12px'}>
                  <PeselField register={props.register} />
                  <EwusPatientDownload
                     register={props.register}
                     formState={props.formState}
                     setValue={props.setValue}
                     watch={props.watch}
                  />
               </StyledFlex>
            )}
            {personalIdentifierType.id === PersonalIdentifierType.GUARDIAN_PESEL && (
               <GuardianPeselField
                  register={props.register}
                  setValue={props.setValue}
                  watch={props.watch}
                  patientId={props.patientId}
               />
            )}
            {personalIdentifierType.id === PersonalIdentifierType.PASSPORT && (
               <>
                  <PassportField register={props.register} />
                  <PassportCountryField register={props.register} />
                  <PassportValidToDateField register={props.register} />
               </>
            )}
            {personalIdentifierType.id === PersonalIdentifierType.NATIONAL_IDENTIFICATION_NUMBER && (
               <>
                  <NationalIdentificationNumberField register={props.register} />
                  <NationalIdentificationNumberCountryField register={props.register} />
                  <div />
               </>
            )}
            {personalIdentifierType.id === PersonalIdentifierType.MEDICAL_RECORD_NUMBER && (
               <MedicalRecordNumberField
                  register={props.register}
                  setValue={props.setValue}
                  patientMedicalRecordNumber={props.patientMedicalRecordNumber}
               />
            )}
            <FirstNameField register={props.register} />
            <MiddleNameField register={props.register} />
            <LastNameField register={props.register} />

            <div />
            <StyledFlex gap={'24px'}>
               <Gender register={props.register} watch={props.watch} />
               <BirthDate register={props.register} watch={props.watch} />
            </StyledFlex>
         </StyledPersonalDataFormSectionContent>
      </StyledPersonalDataFormSection>
   );
};
